import { useEffect, useState } from "react";
import { getContactsService } from "../api/services";
import { UserAvatar } from "../components/UserAvatar";
import { useRequestError } from "../hooks/useRequestError";
import { useToast } from "../components/Toast";
import type { ContactModel } from "../types/models";

type ContactItem = {
  id: string;
  fullName: string;
  role: string;
  phone_number: string | null;
  profile_image_url: string | null;
};

const formatRoleLabel = (role: string | null) => {
  if (!role) return "Contact";
  return role
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const buildFullName = (firstName: string | null, lastName: string | null) =>
  `${firstName || ""} ${lastName || ""}`.trim() || "Unknown";

const sanitizePhoneNumber = (phone: string) =>
  phone.replace(/[^\d+]/g, "").replace(/^\+/, "");

export const ConnectPage = () => {
  const { handleRequestError } = useRequestError();
  const { showToast } = useToast();
  const [tab, setTab] = useState<"whatsapp" | "call">("whatsapp");
  const [contacts, setContacts] = useState<ContactItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response: ContactModel[] = await getContactsService();
        setContacts(
          (response || []).map((contact) => ({
            id: contact.id,
            fullName: buildFullName(contact.first_name, contact.last_name),
            role: formatRoleLabel(contact.role),
            phone_number: contact.phone_number,
            profile_image_url: contact.profile_image_url || null,
          }))
        );
      } catch (error) {
        handleRequestError(error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [handleRequestError]);

  const openWhatsApp = (contact: ContactItem) => {
    if (!contact.phone_number) {
      showToast({
        type: "error",
        title: "Phone Number Unavailable",
        message: `${contact.fullName} has not added a phone number yet.`,
      });
      return;
    }
    const clean = sanitizePhoneNumber(contact.phone_number);
    if (!clean) {
      showToast({
        type: "error",
        title: "Phone Number Unavailable",
        message: `${contact.fullName} has an invalid phone number for WhatsApp.`,
      });
      return;
    }
    window.open(`https://wa.me/${clean}`, "_blank", "noopener,noreferrer");
  };

  const openCall = (contact: ContactItem) => {
    if (!contact.phone_number) {
      showToast({
        type: "error",
        title: "Phone Number Unavailable",
        message: `${contact.fullName} has not added a phone number yet.`,
      });
      return;
    }
    window.location.href = `tel:${contact.phone_number}`;
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Connect</h1>
          <p className="muted">Call or message via external apps</p>
        </div>
      </header>

      <div className="segmented tabs">
        <button
          type="button"
          className={tab === "whatsapp" ? "active" : ""}
          onClick={() => setTab("whatsapp")}
        >
          WhatsApp
        </button>
        <button
          type="button"
          className={tab === "call" ? "active" : ""}
          onClick={() => setTab("call")}
        >
          Call
        </button>
      </div>

      {loading ? <div className="spinner" /> : null}
      {!loading && contacts.length === 0 ? (
        <div className="empty-state">No contacts available.</div>
      ) : (
        <div className="card-list">
          {contacts.map((contact) => (
            <div key={contact.id} className="panel contact-row">
              <div className="contact-info">
                <UserAvatar
                  uri={contact.profile_image_url}
                  name={contact.fullName}
                  size={44}
                />
                <div>
                  <div className="patient-card-title">{contact.fullName}</div>
                  <div className="muted">{contact.role}</div>
                </div>
              </div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() =>
                  tab === "whatsapp" ? openWhatsApp(contact) : openCall(contact)
                }
              >
                {tab === "whatsapp" ? "WhatsApp" : "Call"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
